import TEAMS from "../constants/Teams";

const FILTERS_KEY = "ff_analytics_seat_filters";

/** English zone labels for special-game buttons, in button order. Not translated. */
const SPECIAL_ZONES = {
  "vancouver-whitecaps-fc": ["Upper Zone", "Club Seats", "Lower Zone"],
  "bc-lions": ["", "Other", "Lower Zone"],
  "vancouver-canucks": ["", "", "Lower Zone"],
  "toronto-marlies": ["Upper Zone", "Lower Zone", "Suites"],
  "belleville-senators": ["Upper Zone", "Lower Zone", "Middle Zone"],
  "toronto-raptors": ["Upper Zone", "Lower Zone", "Club Lower"],
  "saskatchewan-roughriders": ["Upper Zone", "", "Lower Zone"],
  "toronto-fc": ["Upper Zone", "Lower Zone", "Other"],
  "toronto-argonauts": ["", "", "Other"],
  "winnipeg-jets": ["Upper Zone", "", "Lower Zone"],
};

export function slugZoneName(name) {
  const text = String(name || "").trim();
  if (!text || text === "-") {
    return "";
  }
  return text
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function zoneNamesForGame(game) {
  if (!game) {
    return ["", "", ""];
  }
  if (game.isSpecial) {
    return SPECIAL_ZONES[game.homeTeamSlug] || ["", "", ""];
  }
  const team = TEAMS.find((item) => item.homeTeamSlug === game.homeTeamSlug);
  if (!team || !Array.isArray(team.zones)) {
    return ["", "", ""];
  }
  return team.zones.slice(0, 3);
}

/** Button index to the tier string Seats.tier uses. 1 → "3", 2 → "2", 3 → "1". */
export function zoneButtonTier(zone) {
  switch (zone) {
    case 1:
      return "3";
    case 2:
      return "2";
    case 3:
      return "1";
    default:
      return undefined;
  }
}

export function zoneFilterValue(game, zoneIndex) {
  const index = Number(zoneIndex);
  if (!index) {
    return "";
  }
  const raw = zoneNamesForGame(game)[index - 1];
  const slug = slugZoneName(raw);
  if (slug) {
    return slug;
  }
  if (String(raw || "").trim() === "-") {
    const tier = zoneButtonTier(index);
    return tier ? `tier_${tier}` : "";
  }
  return "";
}

export function serializeActiveFilters(filters) {
  const current = filters || {};
  const parts = [];
  if (current.quantity) {
    parts.push(`quantity:${current.quantity}`);
  }
  if (current.zone) {
    parts.push(`zone:${current.zone}`);
  }
  if (current.instant === "on") {
    parts.push("instant:on");
  }
  if (current.aisle === "on") {
    parts.push("aisle:on");
  }
  if (current.wheelchair === "on") {
    parts.push("wheelchair:on");
  }
  if (current.extra_wide === "on") {
    parts.push("extra_wide:on");
  }
  return parts.join(",") || "none";
}

/**
 * Returns the event to send, or null when the selection did not change.
 * A clear's value is the new state ("all", "none", or "off"). previous_value is what was removed.
 */
export function filterChange(previous, next, clearedValue) {
  const prev = previous || "";
  const nxt = next || "";
  if (prev === nxt) {
    return null;
  }
  if (!nxt) {
    return {
      event: "filter_cleared",
      value: clearedValue || "off",
      previous_value: prev,
    };
  }
  const change = { event: "filter_applied", value: nxt };
  if (prev) {
    change.previous_value = prev;
  }
  return change;
}

export function canEmitFilterResults({ isLoading, isLoadingTNSeats, tnPending }) {
  return !isLoading && !isLoadingTNSeats && !tnPending;
}

export function filterResultsPayload({
  gameId,
  filter,
  value,
  surface,
  deviceType,
  resultCount,
  previousResultCount,
  activeFilters,
}) {
  const payload = {
    game_id: gameId,
    filter,
    value,
    surface,
    device_type: deviceType,
    result_count: resultCount,
    active_filters: activeFilters,
    zero_results: resultCount === 0,
  };
  if (previousResultCount != null) {
    payload.previous_result_count = previousResultCount;
  }
  return payload;
}

export function rememberSeatFilters(gameId, activeFilters) {
  if (typeof sessionStorage === "undefined" || !gameId) {
    return;
  }
  try {
    sessionStorage.setItem(
      FILTERS_KEY,
      JSON.stringify({ game_id: gameId, active_filters: activeFilters || "none" })
    );
  } catch {
    // ignore
  }
}

export function seatFiltersForGame(gameId) {
  if (typeof sessionStorage === "undefined" || !gameId) {
    return undefined;
  }
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.game_id !== gameId) {
      return undefined;
    }
    if (!parsed.active_filters || parsed.active_filters === "none") {
      return undefined;
    }
    return parsed.active_filters;
  } catch {
    return undefined;
  }
}

export function clearSeatFiltersForTests() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.removeItem(FILTERS_KEY);
}
